/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import formatCurrency from 'shared/utils/formatCurrency';

import { formatDateTime } from 'shared/utils/dateTime';

import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { TableCosting } from 'shared/components';

type ReportTableProps = {
  haierCostData: any;
};

const ReportTable: React.FC<ReportTableProps> = ({ haierCostData }) => {
  const {
    order,
    jobNumber,
    truckType,
    driver,
    licensePlate,
    tripNo,
    waybill,
    vendor,
    cost,
    revenue,
    grandTotal,
  } = haierCostData;

  const columnsTableService = [
    {
      title: 'Order Info.',
      align: 'left',
      style: {
        padding: '6px',
        backgroundColor: '#F6F6F6',
        margin: '-6px',
      },
      children: [
        {
          title: 'Order No.',
          dataIndex: 'orderNo',
          key: 'orderNo',
          width: 130,
        },
        {
          title: 'Invoice No.',
          dataIndex: 'invoiceNo',
          key: 'invoiceNo',
          width: 100,
        },
        {
          title: 'Invoice Date',
          dataIndex: 'invoiceDate',
          key: 'invoiceDate',
          width: 100,
        },
        {
          title: 'Pickup Name',
          dataIndex: 'pickupName',
          key: 'pickupName',
          width: 100,
        },
        {
          title: 'Pickup Provinces',
          dataIndex: 'pickupProvince',
          key: 'pickupProvince',
          width: 130,
        },
        {
          title: 'Consignee Name',
          dataIndex: 'consigneeName',
          key: 'consigneeName',
          width: 130,
        },
        {
          title: 'Type',
          dataIndex: 'type',
          key: 'type',
          width: 80,
        },
        {
          title: 'Pricing Type',
          dataIndex: 'pricingType',
          key: 'pricingType',
          width: 120,
        },
        {
          title: 'Billing Month',
          dataIndex: 'billingMonth',
          key: 'billingMonth',
          width: 120,
        },
        {
          title: 'Delivery Province',
          dataIndex: 'deliveryProvince',
          key: 'deliveryProvince',
          width: 150,
        },
        {
          title: 'Delivery Zone',
          dataIndex: 'deliveryZone',
          key: 'deliveryZone',
          width: 120,
        },
        {
          title: 'Material Code',
          dataIndex: 'itemCode',
          key: 'itemCode',
          width: 120,
        },
        {
          title: 'Material Description',
          dataIndex: 'itemName',
          key: 'itemName',
          width: 300,
        },
        {
          title: 'Order CBM',
          dataIndex: 'cbm',
          key: 'cbm',
          width: 100,
        },
        {
          title: 'Order QTY',
          dataIndex: 'quantity',
          key: 'quantity',
          width: 100,
        },
        {
          title: 'Lift Qty',
          dataIndex: 'liftQuantity',
          key: 'liftQuantity',
          width: 100,
        },
        {
          title: 'Unpack Qty',
          dataIndex: 'unpackQuantity',
          key: 'unpackQuantity',
          width: 100,
        },
        {
          title: 'Other Service Qty',
          dataIndex: 'otherServiceQuantity',
          key: 'otherServiceQuantity',
          width: 130,
        },
        {
          title: 'RT Qty',
          dataIndex: 'rtQuantity',
          key: 'rtQuantity',
          width: 100,
        },
        {
          title: 'RT Name',
          dataIndex: 'rtName',
          key: 'rtName',
          width: 100,
        },
      ],
    },
    {
      title: 'Trip & Job Info.',
      align: 'left',
      style: {
        padding: '6px',
        backgroundColor: '#F6F6F6',
        margin: '-6px',
      },
      children: [
        {
          title: 'Trip No',
          dataIndex: 'tripNo',
          key: 'tripNo',
          width: 120,
        },
        {
          title: 'Waybill No.',
          dataIndex: 'waybill',
          key: 'waybill',
          width: 120,
        },
        {
          title: 'Loaded Date',
          dataIndex: 'pickupDate',
          key: 'pickupDate',
          width: 180,
        },
        {
          title: 'Delivered Date',
          dataIndex: 'deliveryDate',
          key: 'deliveryDate',
          width: 180,
        },
        {
          title: 'Job No.',
          dataIndex: 'jobNumber',
          key: 'jobNumber',
          width: 160,
        },
        {
          title: 'Vendor Code',
          dataIndex: 'vendorName',
          key: 'vendorName',
          width: 280,
        },
        {
          title: 'Truck Types',
          dataIndex: 'truckType',
          key: 'truckType',
          width: 150,
        },
        {
          title: 'License Plates',
          dataIndex: 'licensePlate',
          key: 'licensePlate',
          width: 120,
        },
        {
          title: 'Driver Names',
          dataIndex: 'driver',
          key: 'driver',
          width: 150,
        },
      ],
    },
    {
      title: 'Transport Cost Info.',
      align: 'left',
      style: {
        padding: '6px',
        alignItems: 'flex-end',
        backgroundColor: '#FDDF7C',
        margin: '-6px',
      },
      children: [
        {
          title: 'Transport',
          dataIndex: 'costPrice',
          key: 'costPrice',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#FFEEB6',
            margin: '-6px',
          },
        },
        {
          title: 'Total',
          dataIndex: 'totalCostPrice',
          key: 'totalCostPrice',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#FFEEB6',
            margin: '-6px',
          },
        },
      ],
    },
    {
      title: 'Service Cost Info.',
      align: 'left',
      style: {
        padding: '6px',
        alignItems: 'flex-end',
        backgroundColor: '#FDDF7C',
        margin: '-6px',
      },
      children: [
        {
          title: 'OT',
          dataIndex: 'costOT',
          key: 'costOT',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#FFEEB6',
            margin: '-6px',
          },
        },
        {
          title: 'Overnight Fee',
          dataIndex: 'costOvernightFee',
          key: 'costOvernightFee',
          width: 130,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#FFEEB6',
            margin: '-6px',
          },
        },
        {
          title: 'Unpack',
          dataIndex: 'costUnpack',
          key: 'costUnpack',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#FFEEB6',
            margin: '-6px',
          },
        },
        {
          title: 'Label',
          dataIndex: 'costLabel',
          key: 'costLabel',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#FFEEB6',
            margin: '-6px',
          },
        },
        {
          title: 'Other',
          dataIndex: 'costOther',
          key: 'costOther',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#FFEEB6',
            margin: '-6px',
          },
        },
        {
          title: 'Total',
          dataIndex: 'costTotal',
          key: 'costTotal',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#FFEEB6',
            margin: '-6px',
          },
        },
      ],
    },
    {
      title: 'Transport Revenue Info.',
      align: 'left',
      style: {
        padding: '6px',
        alignItems: 'flex-end',
        backgroundColor: '#74B662',
        margin: '-6px',
      },
      children: [
        {
          title: 'Transport',
          dataIndex: 'revenuePrice',
          key: 'revenuePrice',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'Total ',
          dataIndex: 'revenueTotalPrice',
          key: 'revenueTotalPrice',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
      ],
    },
    {
      title: 'Service Revenue Info.',
      align: 'left',
      style: {
        padding: '6px',
        alignItems: 'flex-end',
        backgroundColor: '#74B662',
        margin: '-6px',
      },
      children: [
        {
          title: 'จำนวนชั้นที่ยก',
          dataIndex: 'revenueCarryHight',
          key: 'revenueCarryHight',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'ค่ายก',
          dataIndex: 'revenueCarry',
          key: 'revenueCarry',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'OT',
          dataIndex: 'revenueOT',
          key: 'revenueOT',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'Overnight Fee',
          dataIndex: 'revenueOvernightFee',
          key: 'revenueOvernightFee',
          width: 130,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'Unpack',
          dataIndex: 'revenueUnpack',
          key: 'revenueUnpack',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'Label',
          dataIndex: 'revenueLabel',
          key: 'revenueLabel',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'Fuel (1%)',
          dataIndex: 'revenueFuel',
          key: 'revenueFuel',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'WH',
          dataIndex: 'revenueWH',
          key: 'revenueWH',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'Other',
          dataIndex: 'revenueOther',
          key: 'revenueOther',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
        {
          title: 'Total',
          dataIndex: 'revenueTotal',
          key: 'revenueTotal',
          width: 100,
          align: 'right',
          style: {
            padding: '6px',
            alignItems: 'flex-end',
            backgroundColor: '#B9DEAF',
            margin: '-6px',
          },
        },
      ],
    },
  ];

  const sumPrice = (priceObj) => {
    const sumPrice =
      (Number(priceObj.carry) || 0) +
      (Number(priceObj.ot) || 0) +
      (Number(priceObj.overnightFee) || 0) +
      (Number(priceObj.unpack) || 0) +
      (Number(priceObj.label) || 0) +
      (Number(priceObj.fuel) || 0) +
      (Number(priceObj.wh) || 0) +
      (Number(priceObj.other) || 0);
    return sumPrice;
  };

  const dataTableService = order.map((item, index) => ({
    key: uuidv4(),
    orderNo: item.orderNo,
    pickupName: item.pickupName,
    pickupProvince: item.pickupProvince,
    consigneeName: item.consigneeName,
    type: item.type,
    pricingType: item.pricingType,
    billingMonth: item.billingMonth,
    deliveryProvince: item.deliveryProvince,
    deliveryZone: item.deliveryZone,
    cbm: item.cbm,
    itemName: item.itemName,
    quantity: item.quantity,
    liftQuantity: item.liftQuantity,
    unpackQuantity: item.unpackQuantity,
    otherServiceQuantity: item.otherServiceQuantity || 0,
    rtQuantity: item.rtQuantity,
    rtName: item.rtName,
    tripNo,
    waybill,
    invoiceNo: item.invoiceNo,
    invoiceDate: formatDateTime(item.invoiceDate),
    itemCode: item.itemCode,
    pickupDate: formatDateTime(item.pickupDate),
    deliveryDate: formatDateTime(item.deliveryDate),
    jobNumber,
    vendorName: vendor.vendorName,
    truckType,
    licensePlate,
    driver,
    costPrice: (
      <div
        style={{
          backgroundColor: '#FFF8E1',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {item.pricingType === 'CBM Rate'
          ? formatCurrency(item.priceCost.transport)
          : index === 0
          ? formatCurrency(Number(cost), '-')
          : '-'}
      </div>
    ),
    totalCostPrice: (
      <div
        style={{
          backgroundColor: '#FFF8E1',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {index === 0 ? formatCurrency(Number(grandTotal.cost), '-') : '-'}
      </div>
    ),
    costOT: (
      <div
        style={{
          backgroundColor: '#FFF8E1',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceCost?.ot || 0), '-')}
      </div>
    ),
    costOvernightFee: (
      <div
        style={{
          backgroundColor: '#FFF8E1',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceCost?.overnightFee || 0), '-')}
      </div>
    ),
    costUnpack: (
      <div
        style={{
          backgroundColor: '#FFF8E1',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceCost?.unpack || 0), '-')}
      </div>
    ),
    costLabel: (
      <div
        style={{
          backgroundColor: '#FFF8E1',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceCost?.label || 0), '-')}
      </div>
    ),
    costOther: (
      <div
        style={{
          backgroundColor: '#FFF8E1',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceCost?.other || 0), '-')}
      </div>
    ),
    costTotal: (
      <div
        style={{
          backgroundColor: '#FFF8E1',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(sumPrice(item.priceCost) || 0), '-')}
      </div>
    ),
    revenuePrice: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {item.pricingType === 'CBM Rate'
          ? formatCurrency(item.priceSale.transport)
          : index === 0
          ? formatCurrency(Number(revenue), '-')
          : '-'}
      </div>
    ),
    revenueTotalPrice: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {index === 0 ? formatCurrency(Number(grandTotal.sale), '-') : '-'}
      </div>
    ),
    revenueCarryHight: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.carryHight || 0), '-')}
      </div>
    ),
    revenueCarry: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceSale?.carry || 0), '-')}
      </div>
    ),
    revenueOT: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceSale?.ot || 0), '-')}
      </div>
    ),
    revenueOvernightFee: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceSale?.overnightFee || 0), '-')}
      </div>
    ),
    revenueUnpack: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceSale?.unpack || 0), '-')}
      </div>
    ),
    revenueLabel: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceSale?.label || 0), '-')}
      </div>
    ),
    revenueFuel: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceSale?.fuel || 0), '-')}
      </div>
    ),
    revenueWH: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceSale?.wh || 0), '-')}
      </div>
    ),
    revenueOther: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(item.priceSale?.other || 0), '-')}
      </div>
    ),
    revenueTotal: (
      <div
        style={{
          backgroundColor: '#EDF9E9',
          margin: '-6px',
          padding: '6px',
        }}
      >
        {formatCurrency(Number(sumPrice(item.priceSale) || 0), '-')}
      </div>
    ),
  }));

  return (
    <TableCosting
      bordered
      className="table-1"
      pagination={false}
      columns={columnsTableService}
      dataSource={dataTableService}
    />
  );
};

export default ReportTable;
